import type { AxiosResponse } from 'axios';
import type { Ref } from 'vue'; // you forgot this import
import { toRef } from 'vue';
import useAuthStore from '../stores/auth';
import type { ResponseStatus } from '../lms';

// Define a minimal interface for API responses that might contain a message
interface ApiResponseWithMessage {
   message?: string;
}

export async function handleApiRequest<S extends object, R = any>(
   store: S,
   loadingRef: keyof S | undefined,
   responseStatusRef: keyof S,
   apiCall: () => Promise<AxiosResponse<R>>,
   resetBeforeCall?: { ref: keyof S; value: any },
): Promise<R | undefined> {
   const loadingState =
      loadingRef && (toRef(store, loadingRef) as Ref<boolean>);
   const responseStatus = toRef(
      store,
      responseStatusRef,
   ) as Ref<ResponseStatus>;

   responseStatus.value = defaultResponseStatus;

   if (resetBeforeCall) {
      const resetRef = toRef(store, resetBeforeCall.ref);

      if (
         Array.isArray(resetRef.value) &&
         Array.isArray(resetBeforeCall.value)
      ) {
         // Mutate array to preserve reactivity
         resetRef.value.splice(
            0,
            resetRef.value.length,
            ...resetBeforeCall.value,
         );
      } else {
         // Normal assignment for non-array types
         resetRef.value = resetBeforeCall.value;
      }
   }

   setOptionalLoadingState(loadingState, true);

   if (!useAuthStore().accessToken) {
      console.error('Authentication token is missing for API request');
      responseStatus.value = {
         statusCode: 401,
         message: 'Unauthorized, please log in',
      };

      setOptionalLoadingState(loadingState, false);

      return undefined;
   }

   try {
      const response = await apiCall();

      // Cast response.data to access the optional message property safely
      const responseData = response.data as ApiResponseWithMessage;
      responseStatus.value = {
         statusCode: response.status,
         message: responseData?.message || 'Success',
      };

      return response.data;
   } catch (error: any) {
      console.error('API request error:', error);

      if (error.response) {
         responseStatus.value = {
            statusCode: error.response.status,
            message:
               error.response.data?.message ||
               error.response.data?.error ||
               'Request failed',
         };
      } else {
         responseStatus.value = {
            statusCode: 500,
            message: 'Server error',
         };
      }

      return undefined;
   } finally {
      setOptionalLoadingState(loadingState, false);
   }
}

function setOptionalLoadingState(
   loadingState: Ref<boolean, boolean> | undefined,
   value: boolean,
) {
   if (loadingState !== undefined) {
      loadingState.value = value;
   }
}

export const defaultResponseStatus: ResponseStatus = {
   statusCode: undefined,
   message: '',
};
